import { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import moment from "moment";
import Button from "@rio-cloud/rio-uikit/Button";
import TableSearch from "@rio-cloud/rio-uikit/TableSearch";
import TableToolbar from "@rio-cloud/rio-uikit/TableToolbar";
import TableViewToggles from "@rio-cloud/rio-uikit/TableViewToggles";
import { SCREEN_SM } from "@rio-cloud/rio-uikit/utils/deviceUtils";
import TableSettingsDialog from "@rio-cloud/rio-uikit/TableSettingsDialog";
import TableCardsSorting from "@rio-cloud/rio-uikit/TableCardsSorting";
import type { SortDirectionType } from "@rio-cloud/rio-uikit/SortDirection";
import type { TableColumnDetailsMap } from "@rio-cloud/rio-uikit/components/table/TableSettingsDialog.types";
import NotFoundState from "@rio-cloud/rio-uikit/NotFoundState";
import TableCol from "@rio-cloud/rio-uikit/TableCol";
import TableHead from "@rio-cloud/rio-uikit/TableHead";
import ContentLoader from "@rio-cloud/rio-uikit/ContentLoader";
import useTableSelection from "@rio-cloud/rio-uikit/useTableSelection";
import Pager from "@components/Maintenance/Pager";
import PagerSkeleton from "@components/Skeletons/PagerSkeleton";
import DetailActionCell from "@components/Table/DetailActionCell";
import { RoundedLoader } from "@components/Skeletons/sharedSkeletonParts";
import { useT } from "@hooks";
import { useStoredTableSettings } from "@hooks/useStoredTableSettings";
import { useStoredTableSort } from "@hooks/useStoredTableSort";
import { resolvePaymentMethodLabel } from "@utils/paymentMethodLabel";
import type { MaintenanceStatus, MaintenanceTransaction } from "@services/maintenance";

const SKELETON_ROWS = 10;
const SKELETON_CELL_HEIGHT = 18;
const SKELETON_CELL_WIDTHS = ["70%", "85%", "55%", "78%", "62%", "48%", "72%"];
const SKELETON_STATUS_HEIGHT = 21;
const SKELETON_STATUS_WIDTH = 72;

type MaintenanceColumnKey = "dateTime" | "vehicle" | "type" | "subcategory" | "paymentMethod" | "amount" | "status";

const DEFAULT_COLUMN_ORDER: MaintenanceColumnKey[] = [
  "dateTime",
  "vehicle",
  "type",
  "subcategory",
  "paymentMethod",
  "amount",
  "status",
];

const DEFAULT_COLUMN_WIDTHS: Record<MaintenanceColumnKey, number> = {
  dateTime: 170,
  vehicle: 180,
  type: 130,
  subcategory: 180,
  paymentMethod: 130,
  amount: 130,
  status: 130,
};

const STATUS_COLOR: Record<MaintenanceStatus, string> = {
  DONE: "success",
  SCHEDULED: "warning",
  CANCELLED: "danger",
};

interface MaintenanceTableProps {
  data: MaintenanceTransaction[];
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
  onRefresh?: () => void;
  onDownload?: () => void;
  onViewDetail?: (id: string) => void;
  searchValue: string;
  onSearchValueChange: (value: string) => void;
}

interface DisplayRow {
  id: string;
  dateTime: string;
  vehicle: string;
  type: string;
  subcategory: string;
  paymentMethod: string;
  amount: number;
  status: string;
  rawStatus: MaintenanceStatus;
  sortableDateTime: string;
}

const formatCurrency = (value: number) =>
  `R$ ${value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

type SortKey = keyof Omit<DisplayRow, "rawStatus" | "id">;

const SORT_KEY_MAP: Partial<Record<MaintenanceColumnKey, SortKey>> = { dateTime: "sortableDateTime" };
const VISIBLE_SORT_KEY: Record<string, string> = { sortableDateTime: "dateTime" };

const MAINTENANCE_SORT_KEYS = Array.from(
  new Set<string>([...DEFAULT_COLUMN_ORDER, ...Object.values(SORT_KEY_MAP)])
) as SortKey[];

const StatusChip = ({ status, label }: Readonly<{ status: MaintenanceStatus; label: string }>) => {
  const color = STATUS_COLOR[status];
  return (
    <span className="display-block text-left">
      <span
        className={`display-inline-block padding-x-10 padding-y-2 rounded-circle border border-width-1 text-size-12 text-medium white-space-nowrap border-color-${color} text-color-${color}`}
      >
        {label}
      </span>
    </span>
  );
};

export default function MaintenanceTable({
  data,
  currentPage,
  totalPages,
  onPageChange,
  isLoading = false,
  onRefresh,
  onDownload,
  onViewDetail,
  searchValue,
  onSearchValueChange,
}: Readonly<MaintenanceTableProps>) {
  const t = useT();
  const COLUMN_LABELS: Record<MaintenanceColumnKey, string> = {
    dateTime: t("maintenance.table.column.dateTime"),
    vehicle: t("maintenance.table.column.vehicle"),
    type: t("maintenance.table.column.type"),
    subcategory: t("maintenance.table.column.subcategory"),
    paymentMethod: t("maintenance.table.column.paymentMethod"),
    amount: t("maintenance.table.column.amount"),
    status: t("maintenance.table.column.status"),
  };

  const [showTableSettingsDialog, setShowTableSettingsDialog] = useState(false);
  const {
    columnOrder,
    hiddenColumns,
    columnsDetails,
    viewType,
    handleColumnChange,
    handleColumnDetailsChange,
    handleViewTypeChange,
  } = useStoredTableSettings<TableColumnDetailsMap>({
    storageKey: "maintenance",
    defaultColumnOrder: DEFAULT_COLUMN_ORDER,
    defaultColumnsDetails: Object.fromEntries(
      DEFAULT_COLUMN_ORDER.map((key) => [key, { width: DEFAULT_COLUMN_WIDTHS[key] }])
    ),
    forcedViewType: window.innerWidth < SCREEN_SM ? TableViewToggles.VIEW_TYPE_SINGLE_CARD : undefined,
  });

  const {
    sortKey: localSortKey,
    sortDirection: localSortDirection,
    setSortKey: setLocalSortKey,
    setSortDirection: setLocalSortDirection,
  } = useStoredTableSort<SortKey>({
    storageKey: "maintenance",
    allowedSortKeys: MAINTENANCE_SORT_KEYS,
    defaultSortKey: "sortableDateTime",
    defaultSortDirection: "desc",
  });

  const displayRows = useMemo<DisplayRow[]>(
    () =>
      data.map((item) => ({
        id: item.id,
        dateTime: moment(item.dateTime).format("DD/MM/YYYY HH:mm"),
        vehicle: item.vehicle,
        type: t(`maintenance.type.${item.type}`),
        subcategory: item.subcategory,
        paymentMethod: resolvePaymentMethodLabel(item.paymentMethod, t) ?? "",
        amount: item.amount,
        status: t(`maintenance.status.${item.status}`),
        rawStatus: item.status,
        sortableDateTime: item.dateTime,
      })),
    [data, t]
  );

  const sortedRows = useMemo(() => {
    if (!localSortKey) return displayRows;

    return [...displayRows].sort((a, b) => {
      const aValue = a[localSortKey];
      const bValue = b[localSortKey];

      if (typeof aValue === "number" && typeof bValue === "number") {
        return localSortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }

      const comparison = String(aValue).localeCompare(String(bValue));
      return localSortDirection === "asc" ? comparison : -comparison;
    });
  }, [displayRows, localSortKey, localSortDirection]);

  const { tableRef, updateRowSelection, onActiveRowChange } = useTableSelection<MaintenanceTransaction>({
    tableData: data,
    idKey: "id" as keyof MaintenanceTransaction,
  });

  const visibleColumns = columnOrder.filter((column) => !hiddenColumns.includes(column)) as MaintenanceColumnKey[];

  useEffect(() => updateRowSelection(), [searchValue, updateRowSelection]);

  const handleSortChange = (event: React.MouseEvent<HTMLTableCellElement>) => {
    const newSortBy = event.currentTarget.dataset.sortby as MaintenanceColumnKey | undefined;
    if (!newSortBy) return;

    const newSortKey = SORT_KEY_MAP[newSortBy] ?? newSortBy;

    if (newSortKey === localSortKey) {
      if (localSortDirection === "asc") {
        setLocalSortDirection("desc");
      } else {
        setLocalSortKey(null);
      }
      return;
    }

    setLocalSortKey(newSortKey);
    setLocalSortDirection("asc");
  };

  const handleCardSortChange = (newSortBy: string, newSortDir: SortDirectionType) => {
    setLocalSortKey(newSortBy as SortKey);
    setLocalSortDirection(newSortDir);
  };

  const cardSortingSelectOptions = visibleColumns.map((column) => {
    const rawSortKey = SORT_KEY_MAP[column] ?? column;
    return {
      id: rawSortKey,
      label: COLUMN_LABELS[column],
      selected: rawSortKey === localSortKey,
      disabled: false,
    };
  });

  const renderCell = (row: DisplayRow, column: MaintenanceColumnKey) => {
    if (column === "amount") return formatCurrency(row.amount);
    if (column === "status") return <StatusChip status={row.rawStatus} label={row.status} />;
    return row[column];
  };

  const tableClassNames = classNames(
    "table",
    "table-layout-fixed",
    "table-column-overflow-hidden",
    "table-bordered",
    "table-sticky",
    "table-head-filled",
    viewType === TableViewToggles.VIEW_TYPE_SINGLE_CARD && "table-cards table-single-card",
    viewType === TableViewToggles.VIEW_TYPE_MULTI_CARDS && "table-cards table-multi-cards"
  );

  const displaySortKey = localSortKey ? (VISIBLE_SORT_KEY[localSortKey] ?? localSortKey) : "";
  const isEmpty = !isLoading && sortedRows.length === 0;

  return (
    <div className="height-100pct">
      <TableToolbar>
        <div className="table-toolbar-container">
          <div className="table-toolbar-group-left">
            <div className="table-toolbar-column display-flex flex-row gap-10 align-items-center">
              <TableSearch
                className="rounded-small width-100pct-xs width-250 width-300-sm"
                value={searchValue}
                onChange={(value: string) => onSearchValueChange(value)}
                placeholder={t("common.label.search")}
              />
            </div>
          </div>

          <div className="table-toolbar-group-right">
            <div className="table-toolbar-column table-toolbar-column-spacer">
              <TableViewToggles
                initialViewType={viewType}
                className="rounded-small"
                onViewTypeChange={handleViewTypeChange}
                tableViewTooltipContent={t("table.toggle.table")}
                singleCardViewTooltipContent={t("table.toggle.list")}
                multiCardsViewTooltipContent={t("table.toggle.cards")}
              />
            </div>
            <div className="table-toolbar-column">
              <div className="btn-group rounded-small">
                <Button iconOnly iconName="rioglyph rioglyph-refresh" onClick={onRefresh} />
                <Button iconOnly iconName="rioglyph rioglyph-download" onClick={onDownload} />
                <Button
                  iconOnly
                  iconName="rioglyph rioglyph-cog"
                  onClick={() => setShowTableSettingsDialog((prev) => !prev)}
                />
              </div>
            </div>
          </div>

          {showTableSettingsDialog && (
            <TableSettingsDialog
              show={showTableSettingsDialog}
              title={t("table.settings.title")}
              onHide={() => setShowTableSettingsDialog(false)}
              onColumnChange={handleColumnChange}
              defaultColumnOrder={DEFAULT_COLUMN_ORDER}
              defaultHiddenColumns={[]}
              columnOrder={columnOrder}
              hiddenColumns={hiddenColumns}
              columnLabels={COLUMN_LABELS}
              disabledColumns={[]}
              closeButtonText={t("table.settings.close")}
              resetButtonText={t("table.settings.reset")}
              searchPlaceholder={t("table.settings.columnName")}
              notFoundMessage={t("table.settings.noColumns")}
              columnsDetails={columnsDetails}
              autoLabel={t("table.settings.auto")}
              onColumnDetailsChange={handleColumnDetailsChange}
              onSearchChange={() => {}}
              immediateChange
            />
          )}
        </div>
      </TableToolbar>

      {localSortKey && viewType !== TableViewToggles.VIEW_TYPE_TABLE && (
        <TableCardsSorting
          className="display-none display-block-sm"
          selectOptions={cardSortingSelectOptions}
          sortName={localSortKey}
          sortOrder={localSortDirection}
          onSortChange={handleCardSortChange}
        />
      )}

      <div className="table-responsive min-height-300">
        {isEmpty && viewType !== TableViewToggles.VIEW_TYPE_TABLE && (
          <NotFoundState message={t("maintenance.table.notFound")} outerClassName="bg-none" />
        )}
        <table ref={tableRef} className={tableClassNames}>
          <colgroup>
            {visibleColumns.map((column) => (
              <TableCol
                key={column}
                columnDetails={columnsDetails[column] ?? { width: DEFAULT_COLUMN_WIDTHS[column] }}
              />
            ))}
            <TableCol className="table-action" />
          </colgroup>
          <thead>
            <tr>
              {visibleColumns.map((column) => (
                <TableHead
                  key={column}
                  label={COLUMN_LABELS[column]}
                  column={column}
                  sortBy={displaySortKey}
                  sortDir={localSortKey ? localSortDirection : undefined}
                  onClick={handleSortChange}
                />
              ))}
              <TableHead className="table-action" />
            </tr>
          </thead>
          <tbody>
            {isLoading &&
              Array.from({ length: SKELETON_ROWS }, (_, rowIndex) => (
                <tr key={`skeleton-${rowIndex}`}>
                  {visibleColumns.map((column, columnIndex) => (
                    <td key={column} data-field={COLUMN_LABELS[column]}>
                      {column === "status" ? (
                        <span className="display-block text-left">
                          <RoundedLoader
                            height={SKELETON_STATUS_HEIGHT}
                            width={SKELETON_STATUS_WIDTH}
                            radius={SKELETON_STATUS_HEIGHT / 2}
                          />
                        </span>
                      ) : (
                        <ContentLoader
                          className="display-block"
                          height={SKELETON_CELL_HEIGHT}
                          width={SKELETON_CELL_WIDTHS[columnIndex % SKELETON_CELL_WIDTHS.length]}
                        />
                      )}
                    </td>
                  ))}
                  <td className="table-action" />
                </tr>
              ))}
            {isEmpty && viewType === TableViewToggles.VIEW_TYPE_TABLE && (
              <tr className="bg-none">
                <td colSpan={visibleColumns.length + 1}>
                  <NotFoundState outerClassName="border-none bg-none" message={t("maintenance.table.notFound")} />
                </td>
              </tr>
            )}
            {!isLoading &&
              sortedRows.map((row) => (
                <tr key={row.id} data-id={row.id} onClick={onActiveRowChange} className="cursor-pointer">
                  {visibleColumns.map((column) => (
                    <td key={column} data-field={COLUMN_LABELS[column]}>
                      {renderCell(row, column)}
                    </td>
                  ))}
                  <DetailActionCell
                    title={t("maintenance.table.action.viewDetail")}
                    onClick={() => onViewDetail?.(row.id)}
                  />
                </tr>
              ))}
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
          </tbody>
        </table>
      </div>

      {isLoading && <PagerSkeleton className="margin-top-20" loaderClassName="display-block skeleton-on-light-bg" />}
      {!isLoading && data.length > 0 && (
        <Pager
          className="margin-top-20"
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}
